import { ExistingSystemComponentType } from 'data/types';

export type AddEquipmentKind =
  | 'electrical-panel'
  | 'indoor-unit'
  | 'outdoor-unit'
  | 'blower-unit'
  | 'packaged-unit';

export type EquipmentCategory = Exclude<AddEquipmentKind, 'electrical-panel'>;

type ComponentTypeName = Exclude<
  ExistingSystemComponentType['type'],
  undefined
>;

export type CategoryKindOption = {
  label: string;
  type: ComponentTypeName;
};

export type CategoryConfig = {
  noun: string;
  kinds: CategoryKindOption[];
};

// UI taxonomy only (AHA-6681 batch 6, Geoff ruling 2026-07-28): the
// category → kind tiers the modal presents. This intentionally differs from
// the internal derivation slot buckets (an air handler sits in the blower
// slot internally) — do NOT feed this grouping back into derivation code.
export const CATEGORY_CONFIG: Record<EquipmentCategory, CategoryConfig> = {
  'outdoor-unit': {
    noun: 'Outdoor Unit',
    kinds: [
      { label: 'Condenser', type: 'Split System Condenser' },
      { label: 'Heat Pump', type: 'Split System Heat Pump' }
    ]
  },
  'indoor-unit': {
    noun: 'Indoor Unit',
    kinds: [
      { label: 'Evaporator Coil', type: 'Split System Evaporator Coil' },
      { label: 'Air Handler', type: 'Split System Air Handler' }
    ]
  },
  'blower-unit': {
    noun: 'Blower Unit',
    kinds: [
      { label: 'Furnace', type: 'Split System Furnace' },
      { label: 'Modular Blower', type: 'Split System Modular Blower' }
    ]
  },
  // Packaged (Self Contained) units (AHA-6796): exactly the four SC types —
  // PTAC is deliberately excluded from the picker. A packaged unit is a
  // complete standalone system, so the reducer routes these adds to their
  // own system (never alongside split components).
  'packaged-unit': {
    noun: 'Packaged Unit',
    kinds: [
      { label: 'Electric AC', type: 'Self Contained Electric AC' },
      {
        label: 'Electric Heat Pump',
        type: 'Self Contained Electric Heat Pump'
      },
      { label: 'Gas Pack with AC', type: 'Self Contained Gas Pack With AC' },
      {
        label: 'Gas Pack with Heat Pump',
        type: 'Self Contained Gas Pack With Heat Pump'
      }
    ]
  }
};

export const PANEL_NOUN = 'Electrical Panel';

export const PANEL_IMAGE_HINT =
  'Add a photo of the panel or its main breaker and we will try to determine the amperage automatically.';

export const EQUIPMENT_IMAGE_HINT =
  'Add a photo of the data label or enter the model number and we will determine the details automatically.';

// Outdoor-unit (Condenser / Heat Pump) spec-section disclaimer (AHA-6796):
// homeowners should not be asked to look up MCA themselves.
export const OUTDOOR_UNIT_MCA_NOTE =
  "An installer will verify your electrical circuit supports the new equipment's minimum circuit ampacity (MCA) — you don't need to look this up.";

// Standalone-system notes (AHA-6796): the user-facing explanation for why
// an SC add never joins an existing split system — and the mirror direction
// when a split add would skip an SC-containing system.
export const PACKAGED_STANDALONE_NOTE =
  'A packaged unit is a complete system on its own — it will be added as its own system.';

export const SPLIT_ALONGSIDE_PACKAGED_NOTE =
  "This will be added as a separate system — packaged units are complete systems and can't share components.";

// Reverse lookup for edit-open preselection. Returns null for component
// types outside the taxonomy (PTAC, Ductless, ...) — those render the
// raw-type row. Self Contained types are in-taxonomy ('packaged-unit')
// since AHA-6796.
export function getCategoryForComponentType(
  type: ExistingSystemComponentType['type'] | undefined
): EquipmentCategory | null {
  if (!type) {
    return null;
  }
  const entry = (
    Object.entries(CATEGORY_CONFIG) as [EquipmentCategory, CategoryConfig][]
  ).find(([, config]) => config.kinds.some((kind) => kind.type === type));
  return entry ? entry[0] : null;
}

// The modal's Save gate (Geoff ruling): the per-category minimum a component
// must carry to save. This is a strict SUBSET of the config `required` flags
// in existingSystemComponentTypeProperties, which keep driving the
// missing-details card warning and the page-progress gate. Types not listed
// here have no minimum beyond type — the Self Contained types deliberately
// so (AHA-6796): an SC add saves type-only, and the config-required
// voltagePhase/gasType/tonnage keep driving the warning and gate after save.
export const SAVE_MINIMUM_FIELDS: Partial<Record<ComponentTypeName, string[]>> =
  {
    'Split System Condenser': ['tonnage', 'refrigerantType'],
    'Split System Heat Pump': ['tonnage', 'refrigerantType'],
    'Split System Evaporator Coil': ['primaryRefrigerant'],
    'Split System Air Handler': ['primaryRefrigerant'],
    'Split System Furnace': ['fuelType'],
    'Split System Modular Blower': []
  };

// Category seed only — the modal overrides from the edit component's actual
// type, so a furnace opened from the grid still lands on Blower Unit.
export function getEquipmentKindForGridObjectType(
  type: 'existing-condenser' | 'existing-furnace'
): AddEquipmentKind {
  return type === 'existing-condenser' ? 'outdoor-unit' : 'indoor-unit';
}

export function haveSameAttachmentIds(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}
